/**
 * Pinch-to-zoom functionality for touch devices
 *
 * Designed to work with PDPZoom.vue carousel component and vue3-carousel library
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Touch_events/Multi-touch_interaction} Touch APIs
 * @see {@link https://vue3-carousel.ismail9k.com/config.html} vue3-carousel documentation
 */
export default function pinchZoom() {
  let state = {
    scale: 1,
    translate: { x: 0, y: 0 },
    isZooming: false,
    isDragging: false,
    initialDistance: 0,
    lastTouch: { x: 0, y: 0 },
    zoomCenter: { x: 0.5, y: 0.5 },
    lastTapTime: 0,
    tapTimeout: null
  };

  const getDistance = (touch1, touch2) => Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
  const getCenter = (touch1, touch2) => ({
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2
  });
  const isOnImage = (touch, rect) => touch.clientX >= rect.left && touch.clientX <= rect.right && touch.clientY >= rect.top && touch.clientY <= rect.bottom;

  function getActiveImage(target) {
    const slide = target.querySelector('.carousel__slide--active');
    return slide?.querySelector('img');
  }

  function applyTransform(img) {
    const { scale, translate, zoomCenter } = state;
    img.style.transformOrigin = `${zoomCenter.x * 100}% ${zoomCenter.y * 100}%`;
    img.style.transform = `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`;
  }

  function resetZoom(img) {
    state.scale = 1;
    state.translate = { x: 0, y: 0 };
    img.style.transform = 'scale(1)';
    img.style.transformOrigin = 'center center';
  }

  function handleDoubleTap(touch, img) {
    const rect = img.getBoundingClientRect();

    if (state.scale === 1) {
      state.scale = 2;
      state.zoomCenter = {
        x: (touch.clientX - rect.left) / rect.width,
        y: (touch.clientY - rect.top) / rect.height
      };
    } else if (state.scale < 3) {
      state.scale = 3;
    } else {
      resetZoom(img);
      return;
    }

    img.style.transition = 'transform 0.3s ease-out';
    applyTransform(img);
  }

  function handleTouchStart(e) {
    const img = getActiveImage(e.currentTarget);
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const touches = Array.from(e.touches);

    if (touches.length === 2) {
      if (state.tapTimeout) {
        clearTimeout(state.tapTimeout);
        state.tapTimeout = null;
      }

      if (!touches.every(t => isOnImage(t, rect))) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      state.isZooming = true;
      state.isDragging = false;

      const currentDistance = getDistance(touches[0], touches[1]);
      state.initialDistance = currentDistance / state.scale;

      if (state.scale === 1) {
        const center = getCenter(touches[0], touches[1]);
        state.zoomCenter = {
          x: (center.x - rect.left) / rect.width,
          y: (center.y - rect.top) / rect.height
        };
      }
    } else if (touches.length === 1) {
      const touch = touches[0];
      if (!isOnImage(touch, rect)) return;

      const currentTime = Date.now();
      const timeDiff = currentTime - state.lastTapTime;

      if (timeDiff < 300) {
        if (state.tapTimeout) {
          clearTimeout(state.tapTimeout);
          state.tapTimeout = null;
        }
        e.preventDefault();
        handleDoubleTap(touch, img);
        state.lastTapTime = 0;
        return;
      }

      state.lastTapTime = currentTime;

      if (state.scale > 1) {
        e.preventDefault();
        state.isDragging = true;
        state.lastTouch = { x: touch.clientX, y: touch.clientY };
      } else {
        state.tapTimeout = setTimeout(() => {
          state.tapTimeout = null;
        }, 300);
      }
    }
  }

  function handleTouchMove(e) {
    const img = getActiveImage(e.currentTarget);
    if (!img) return;

    const touches = Array.from(e.touches);

    if (touches.length === 2 && state.isZooming) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const currentDistance = getDistance(touches[0], touches[1]);
      state.scale = Math.min(Math.max(currentDistance / state.initialDistance, 1), 3);

      img.style.transition = 'none';
      applyTransform(img);
    } else if (touches.length === 1 && state.isDragging && state.scale > 1) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const touch = touches[0];
      const panSpeed = 1.5;
      const deltaX = (touch.clientX - state.lastTouch.x) * panSpeed;
      const deltaY = (touch.clientY - state.lastTouch.y) * panSpeed;

      const maxTranslate = (state.scale - 1) * 200;

      state.translate.x = Math.max(-maxTranslate, Math.min(maxTranslate, state.translate.x + deltaX));
      state.translate.y = Math.max(-maxTranslate, Math.min(maxTranslate, state.translate.y + deltaY));
      state.lastTouch = { x: touch.clientX, y: touch.clientY };

      applyTransform(img);
    }
  }

  function handleTouchEnd(e) {
    const img = getActiveImage(e.currentTarget);
    if (!img) return;

    if (state.scale > 1 || state.isZooming) {
      img.style.transition = 'transform 0.3s ease-out';
    }

    if (e.touches.length === 0) {
      if (state.scale < 1.2) resetZoom(img);
      state.isZooming = false;
      state.isDragging = false;
    } else if (e.touches.length === 1 && state.isZooming) {
      state.isZooming = false;
      if (state.scale > 1) {
        state.isDragging = true;
        state.lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    }
  }

  return {
    touchstart: handleTouchStart,
    touchmove: handleTouchMove,
    touchend: handleTouchEnd
  };
}
